/* MagicLinkRequestPage.jsx — "Email me a secure access link".
 *
 * Premium, frictionless. Enter your email → tap → we email a one-time
 * sign-in link. No password, no setup, no marketing fluff. Always returns
 * success regardless of whether the email is registered (anti-enumeration).
 *
 * Mobile-first: sticky CTA at the bottom of the viewport. Quiet design
 * inspired by Linear/Stripe/banking apps — calm, reassuring, premium.
 */

import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Mail,
  Lock,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Send,
} from "lucide-react";

import { api, apiError } from "../../lib/api";
import { PremiumInput } from "../../components/brand/PremiumInput";

export default function MagicLinkRequestPage() {
  const [params] = useSearchParams();
  const dealId = params.get("deal_id");

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!trimmed) return setError("Please enter your email.");
    setSubmitting(true);
    try {
      await api.post("/api/auth/magic/request", {
        email: trimmed,
        deal_id: dealId || undefined,
      });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 to prevent enumeration; this only
      // catches rate-limit (429) or network errors.
      const status = err?.response?.status;
      if (status === 429) {
        setError("Too many requests. Please wait a few minutes and try again.");
      } else {
        setError(apiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      {sent ? <SentState email={email} dealId={dealId} /> : (
        <form
          onSubmit={submit}
          className="gp-card p-5 sm:p-7"
          data-testid="magic-link-request-form"
          noValidate
        >
          <div className="gp-eyebrow mb-3 flex items-center gap-2">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full cf-pulse"
              style={{ background: "var(--gp-violet)" }}
            />
            FRICTIONLESS RETURN
          </div>
          <h1
            className="font-display font-bold text-[22px] sm:text-[26px] leading-[1.08] tracking-tight"
            style={{ color: "var(--gp-text)" }}
            data-testid="magic-link-headline"
          >
            {dealId ? "Return to your Deal Room" : "Sign in without a password"}
          </h1>
          <p
            className="mt-1.5 text-[13px] sm:text-[13.5px]"
            style={{ color: "var(--gp-text-muted)" }}
          >
            Enter your email and we'll send a secure one-time link that signs
            you straight in. No password required.
          </p>

          <div className="mt-5">
            <label
              className="text-[11.5px] font-semibold"
              style={{ color: "var(--gp-text-muted)" }}
            >
              Email
            </label>
            <div className="mt-1">
              <PremiumInput
                type="email"
                autoComplete="email"
                autoFocus
                size="lg"
                prefix={<Mail size={14} style={{ color: "var(--gp-text-subtle)" }} />}
                placeholder="you@yourcompany.com"
                value={email}
                onChange={setEmail}
                testid="magic-link-email"
              />
            </div>
          </div>

          {error ? (
            <p
              className="text-[12px] mt-3"
              style={{ color: "#B91C1C" }}
              data-testid="magic-link-error"
            >
              {error}
            </p>
          ) : null}

          {/* Inline submit (desktop) — mobile uses the sticky CTA below */}
          <button
            type="submit"
            disabled={submitting}
            className="hidden sm:inline-flex mt-5 w-full items-center justify-center gap-2 h-12 px-5 rounded-xl text-white text-[13.5px] font-bold gp-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="magic-link-submit"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                Email me a secure access link <ArrowRight size={14} />
              </>
            )}
          </button>

          <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--gp-card-border)" }}>
            <p
              className="text-[11.5px] flex items-center gap-1.5"
              style={{ color: "var(--gp-text-subtle)" }}
            >
              <Lock size={11} /> Secure · one-time · expires in 15 minutes
            </p>
          </div>
        </form>
      )}

      {/* Mobile sticky CTA — hidden once sent */}
      {!sent && (
        <div
          className="sm:hidden fixed left-0 right-0 bottom-0 z-40 px-4 pt-3"
          style={{
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(14px) saturate(140%)",
            borderTop: "1px solid var(--gp-card-border)",
            paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))",
          }}
          data-testid="magic-link-mobile-cta"
        >
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 h-12 px-5 rounded-xl text-white text-[13.5px] font-bold gp-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="magic-link-submit-mobile"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                Email me the link <ArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      )}

      <p
        className="text-center text-[11.5px] mt-3 pb-24 sm:pb-0"
        style={{ color: "var(--gp-text-subtle)" }}
      >
        Prefer a password?{" "}
        <Link
          to="/login"
          className="font-semibold"
          style={{ color: "var(--gp-violet-deep)" }}
          data-testid="magic-link-to-login"
        >
          Sign in →
        </Link>
      </p>
    </Shell>
  );
}

/* ─── Sent state ──────────────────────────────────────────────────────── */
const SentState = ({ email, dealId }) => (
  <div className="gp-card p-6 sm:p-8 text-center" data-testid="magic-link-sent">
    <div
      className="mx-auto w-12 h-12 rounded-full flex items-center justify-center"
      style={{
        background: "var(--gp-green-soft)",
        border: "1px solid var(--gp-green-border)",
        color: "#047857",
      }}
    >
      <CheckCircle2 size={22} />
    </div>
    <h1
      className="font-display font-bold mt-4 text-[20px] sm:text-[22px] tracking-tight"
      style={{ color: "var(--gp-text)" }}
    >
      Check your inbox
    </h1>
    <p
      className="mt-2 text-[13px] sm:text-[13.5px] leading-snug"
      style={{ color: "var(--gp-text-muted)" }}
    >
      If <strong style={{ color: "var(--gp-text)" }}>{email}</strong> matches a
      ChainFlow account, we've sent a secure access link that will sign you
      straight {dealId ? "into your Deal Room" : "in"}. It expires in
      15&nbsp;minutes.
    </p>
    <div
      className="mt-5 rounded-xl p-3.5 text-left"
      style={{
        background: "var(--gp-violet-soft)",
        border: "1px solid var(--gp-violet-border)",
      }}
    >
      <p
        className="text-[10px] uppercase tracking-[0.16em] font-bold"
        style={{ color: "var(--gp-violet-deep)" }}
      >
        On your phone?
      </p>
      <p
        className="mt-1 text-[12.5px] leading-snug"
        style={{ color: "var(--gp-text)" }}
      >
        Open the email on the same device — tapping the link will return you to
        ChainFlow in a single tap.
      </p>
    </div>
    <p
      className="mt-5 text-[11.5px]"
      style={{ color: "var(--gp-text-subtle)" }}
    >
      Didn't receive it? Check your spam folder or{" "}
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="font-semibold underline-offset-2 hover:underline"
        style={{ color: "var(--gp-violet-deep)" }}
        data-testid="magic-link-retry"
      >
        request a new one
      </button>
      .
    </p>
  </div>
);

/* ─── Shell ───────────────────────────────────────────────────────────── */
const Shell = ({ children }) => (
  <div className="min-h-screen gp-surface" data-testid="magic-link-request-page">
    <div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-1/2 -translate-x-1/2 w-[680px] h-[420px] rounded-full"
      style={{
        background: "radial-gradient(circle, rgba(124,58,237,0.10), transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <div className="relative mx-auto max-w-[460px] px-4 sm:px-6 pt-6 sm:pt-12 pb-10 space-y-4">
      <header className="flex items-center justify-between mb-2">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="magic-link-brand">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
            }}
          >
            <Send size={13} className="text-white" />
          </div>
          <span className="font-display font-bold text-[14px] tracking-tight" style={{ color: "var(--gp-text)" }}>
            ChainFlow <span style={{ color: "var(--gp-text-subtle)" }}>OS</span>
          </span>
        </Link>
      </header>
      {children}
    </div>
  </div>
);
